import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Stack, Step, StepLabel, Stepper,
    TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SftGlobalData from '../../library/SftGlobalData';
import { NoWebshopState } from './_webshopNotice';
import { webshopTokens, WebshopPasswordField } from './_webshopUi';

// Password field with its own show/hide state (used for the FTP/DB secrets).
function PwField({ label, value, onChange }) {
    const [show, setShow] = useState(false);
    return (
        <Box sx={{ mb: 1.5 }}>
            <WebshopPasswordField label={label} value={value} onChange={(e) => onChange(e.target.value)} visible={show} onToggle={() => setShow(v => !v)} />
        </Box>
    );
}
PwField.propTypes = { label: PropTypes.node, value: PropTypes.any, onChange: PropTypes.func };

/**
 * Webshop setup wizard — the single entry point to get a shop ready for the
 * admin. Replaces the loose install/update buttons on the Connecties tab.
 *
 * Flow:
 *   0. Status  — read-only probe; pick the path (Updates vs. Volledige install).
 *   1. Gegevens — URL (editable; setUrl) + FTP/DB creds (install path only).
 *   2. Uitvoeren — run install/update, show the step log; jump to the admin.
 *
 * Safety still lives server-side: install.php refuses (code 40) on a populated DB.
 */
export default function WebshopSetupScreen(props) {
    const navigate = useNavigate();
    const { cardBg, borderColor, textColor, mutedText } = webshopTokens(props.theme);

    const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

    const webshopId = props.selectedWebshop?.id;
    const post = useCallback((action, body) => SftGlobalData.post(action, {
        key: props.api, webshop_id: webshopId,
        sftUserId: props.login?.sft_user_id, user: props.login?.login, ...body,
    }), [props.api, webshopId, props.login?.sft_user_id, props.login?.login]);

    const [activeStep, setActiveStep] = useState(0);
    const [mode, setMode] = useState(null); // 'update' | 'install'

    // ── Step 0: status probe ────────────────────────────────────────────────
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const probe = useCallback(async () => {
        if (!webshopId) return;
        setStatusLoading(true);
        try {
            const res = await post('webshop&action=status', {});
            setStatus(res?.success ? res.data : null);
        } catch { setStatus(null); }
        finally { setStatusLoading(false); }
    }, [post, webshopId]);
    useEffect(() => { setStatus(null); setActiveStep(0); setMode(null); probe(); }, [probe]);

    // ── Step 1: data (url + creds) ──────────────────────────────────────────
    const [url, setUrl] = useState('');
    const [ftpHost, setFtpHost] = useState('');
    const [ftpPort, setFtpPort] = useState('21');
    const [ftpUser, setFtpUser] = useState('');
    const [ftpPass, setFtpPass] = useState('');
    const [ftpDir, setFtpDir] = useState('/');
    const [dbHost, setDbHost] = useState('');
    const [dbPort, setDbPort] = useState('3306');
    const [dbName, setDbName] = useState('');
    const [dbUser, setDbUser] = useState('');
    const [dbPass, setDbPass] = useState('');
    const [dataLoading, setDataLoading] = useState(false);

    const loadData = useCallback(async () => {
        setUrl(props.selectedWebshop?.display_name || '');
        setDataLoading(true);
        try {
            const res = await post('webshop&action=getCredentials', {});
            const ftp = res?.success ? res.data?.ftp : null;
            const db = res?.success ? res.data?.db : null;
            if (ftp) {
                setFtpHost(ftp.host || ''); setFtpPort(String(ftp.port || 21));
                setFtpUser(ftp.username || ''); setFtpPass(ftp.password || '');
                setFtpDir(ftp.directory || '/');
            }
            if (db) {
                setDbHost(db.host || ''); setDbPort(String(db.port || 3306));
                setDbName(db.name || ''); setDbUser(db.username || ''); setDbPass(db.password || '');
            }
        } catch { /* keep blanks */ }
        finally { setDataLoading(false); }
    }, [post, props.selectedWebshop?.display_name]);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const saveDataAndNext = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const calls = [];
            if (url && url !== (props.selectedWebshop?.display_name || '')) {
                calls.push(post('webshop&action=setUrl', { url }));
            }
            if (mode === 'install') {
                calls.push(post('webshop&action=setFtp', { ftp: {
                    host: ftpHost, port: Number(ftpPort) || 21, username: ftpUser, password: ftpPass, directory: ftpDir,
                } }));
                calls.push(post('webshop&action=setDb', { db: {
                    host: dbHost, port: Number(dbPort) || 3306, name: dbName, username: dbUser, password: dbPass,
                } }));
            }
            const results = await Promise.all(calls);
            const failed = results.find(r => !r || !r.success);
            if (failed) { setSaveError(failed?.message || tr('cfg_setup_save_failed', 'Opslaan mislukt.')); return; }
            if (props.onProvisioned) props.onProvisioned();
            setActiveStep(2);
        } catch (e) {
            setSaveError(e?.message || tr('cfg_setup_save_failed', 'Opslaan mislukt.'));
        } finally {
            setSaving(false);
        }
    };

    const installReady = !!(ftpHost && ftpUser && ftpPass && dbHost && dbUser && dbPass && dbName);
    const dataValid = !!url && (mode === 'update' || installReady);

    // ── Step 2: run ─────────────────────────────────────────────────────────
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setRunning(true);
        setResult(null);
        try {
            const res = await post(mode === 'install' ? 'webshop&action=install' : 'webshop&action=update', {});
            const payload = res?.data;
            setResult(payload && Array.isArray(payload.steps)
                ? payload
                : { ok: false, steps: [{ step: 'error', msg: res?.message || tr('cfg_setup_failed', 'Mislukt.'), ok: false }] });
            if (res?.success && payload?.ok) {
                if (props.notify) props.notify(mode === 'install' ? tr('cfg_setup_installed', 'Webshop geïnstalleerd') : tr('cfg_setup_updated', 'Webshop bijgewerkt'), 4000, 'top-right', props.theme);
                if (props.onProvisioned) props.onProvisioned();
            }
        } catch (e) {
            setResult({ ok: false, steps: [{ step: 'error', msg: e?.message || tr('cfg_setup_failed', 'Mislukt.'), ok: false }] });
        } finally {
            setRunning(false);
        }
    };

    const chooseMode = (m) => { setMode(m); loadData(); setActiveStep(1); };

    const field = (label, value, onChange, opts = {}) => (
        <TextField label={label} value={value} onChange={(e) => onChange(e.target.value)}
            size="small" fullWidth type={opts.type || 'text'} sx={{ mb: 1.5 }} />
    );

    // No webshop linked yet (no lic_clients_webshop row) → there's nothing to
    // select in the sidebar, so this is the onboarding entry: the provisioning
    // flow that back-fills the row from lic_clients_webshop_info.
    if (!webshopId) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                <Typography variant="h5" sx={{ color: textColor, fontWeight: 600, mb: 0.5 }}>{tr('cfg_setup_title', 'Webshop instellen')}</Typography>
                <Typography variant="body2" sx={{ color: mutedText, mb: 3 }}>
                    {props.selectedWebshop?.display_name || props.selectedWebshop?.slug}
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    <Step><StepLabel>{tr('cfg_setup_step_status', 'Status')}</StepLabel></Step>
                    <Step><StepLabel>{tr('cfg_setup_step_data', 'Gegevens')}</StepLabel></Step>
                    <Step><StepLabel>{tr('cfg_setup_step_run', 'Uitvoeren')}</StepLabel></Step>
                </Stepper>

                {/* STEP 0 — status */}
                {activeStep === 0 && (
                    <Box>
                        {statusLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={20} /><Typography variant="body2">{tr('cfg_setup_checking_status', 'Status controleren…')}</Typography>
                            </Box>
                        ) : (
                            <>
                                {(() => {
                                    const cur = status?.version ? ` · v${status.version}` : '';
                                    let severity = 'warning';
                                    let msg;
                                    if (status?.miniApiOk && status?.updateAvailable) {
                                        severity = 'info';
                                        msg = tr('cfg_setup_status_update', 'Mini-api actief%cur%. Er is een nieuwere versie beschikbaar%ver% — voer updates uit.')
                                            .replace('%cur%', cur)
                                            .replace('%ver%', status.latestVersion ? ` (v${status.latestVersion})` : '');
                                    } else if (status?.miniApiOk) {
                                        severity = 'success';
                                        msg = tr('cfg_setup_status_uptodate', 'Mini-api actief%cur% — alles up-to-date.').replace('%cur%', cur);
                                    } else if (status?.storefrontPresent) {
                                        severity = 'info';
                                        msg = tr('cfg_setup_status_sf_present', 'Storefront aanwezig, mini-api ontbreekt — bestaande shop. Voer updates uit om de mini-api toe te voegen.');
                                    } else if (status) {
                                        msg = tr('cfg_setup_status_new_shop', 'Geen storefront gevonden — dit lijkt een nieuwe/lege shop. Een volledige installatie is nodig.');
                                    } else {
                                        msg = tr('cfg_setup_status_unknown', 'Status onbekend — de shop is (nog) niet bereikbaar.');
                                    }
                                    return <Alert severity={severity} sx={{ mb: 2 }}>{msg}</Alert>;
                                })()}
                                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                    <Button variant="contained" onClick={() => chooseMode('update')} sx={{ textTransform: 'none' }}>
                                        {tr('cfg_setup_btn_run_updates', 'Updates uitvoeren')}
                                    </Button>
                                    {/* Full install only when no storefront is present — running it on
                                        an existing shop (files already there) would rebuild the DB. */}
                                    {!status?.storefrontPresent && (
                                        <Button variant="outlined" color="warning" onClick={() => chooseMode('install')} sx={{ textTransform: 'none' }}>
                                            {tr('cfg_setup_btn_full_install', 'Volledige installatie')}
                                        </Button>
                                    )}
                                    <Button variant="text" onClick={probe} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_refresh_status', 'Status vernieuwen')}</Button>
                                </Stack>
                                {!status?.storefrontPresent && (
                                    <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 1.5 }}>
                                        {tr('cfg_setup_full_install_hint', 'Een volledige installatie bouwt de database opnieuw op — enkel voor een lege shop. De server weigert automatisch als er al data is.')}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* STEP 1 — data */}
                {activeStep === 1 && (
                    <Box>
                        {dataLoading && <Box sx={{ mb: 2 }}><CircularProgress size={18} /></Box>}
                        {field(tr('cfg_setup_field_url', 'Webshop-URL'), url, setUrl)}
                        {mode === 'install' && (
                            <>
                                <Typography variant="subtitle2" sx={{ color: textColor, mt: 2, mb: 1 }}>{tr('cfg_setup_section_ftp', 'FTP')}</Typography>
                                {field(tr('cfg_setup_field_host', 'Host'), ftpHost, setFtpHost)}
                                {field(tr('cfg_setup_field_port', 'Poort'), ftpPort, setFtpPort)}
                                {field(tr('cfg_setup_field_user', 'Gebruiker'), ftpUser, setFtpUser)}
                                <PwField label={tr('cfg_setup_field_password', 'Wachtwoord')} value={ftpPass} onChange={setFtpPass} />
                                {field(tr('cfg_setup_field_dir', 'Map'), ftpDir, setFtpDir)}
                                <Typography variant="subtitle2" sx={{ color: textColor, mt: 2, mb: 1 }}>{tr('cfg_setup_section_db', 'Database')}</Typography>
                                {field(tr('cfg_setup_field_host', 'Host'), dbHost, setDbHost)}
                                {field(tr('cfg_setup_field_port', 'Poort'), dbPort, setDbPort)}
                                {field(tr('cfg_setup_field_dbname', 'Databasenaam'), dbName, setDbName)}
                                {field(tr('cfg_setup_field_user', 'Gebruiker'), dbUser, setDbUser)}
                                <PwField label={tr('cfg_setup_field_password', 'Wachtwoord')} value={dbPass} onChange={setDbPass} />
                            </>
                        )}
                        {saveError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{saveError}</Alert>}
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                            <Button onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_previous', 'Vorige')}</Button>
                            <Button variant="contained" disabled={!dataValid || saving} onClick={saveDataAndNext} sx={{ textTransform: 'none' }}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                                {tr('cfg_setup_btn_save_next', 'Opslaan & verder')}
                            </Button>
                        </Stack>
                    </Box>
                )}

                {/* STEP 2 — run */}
                {activeStep === 2 && (
                    <Box>
                        {!result && !running && (
                            <Alert severity={mode === 'install' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                                {mode === 'install'
                                    ? tr('cfg_setup_run_install_info', 'Volledige installatie wordt uitgevoerd. Bij een shop met bestaande data weigert de server automatisch.')
                                    : tr('cfg_setup_run_update_info', 'Updates worden uitgevoerd (mini-api + migraties). Bestaande data blijft behouden.')}
                            </Alert>
                        )}
                        {running && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <CircularProgress size={20} /><Typography variant="body2">{tr('cfg_setup_running', 'Bezig… dit kan enkele minuten duren.')}</Typography>
                            </Box>
                        )}
                        {result && (
                            <Stack spacing={0.5} sx={{ mb: 2 }}>
                                {(result.steps || []).map((s, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        {s.ok
                                            ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                            : <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                                        <Typography variant="body2" sx={{ color: s.ok ? 'success.main' : 'error.main' }}>{s.msg}</Typography>
                                    </Box>
                                ))}
                                <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                                    {result.ok ? tr('cfg_setup_done', 'Klaar.') : tr('cfg_setup_failed', 'Mislukt.')}
                                </Alert>
                            </Stack>
                        )}
                        <Stack direction="row" spacing={1.5}>
                            {!result && <Button onClick={() => setActiveStep(1)} disabled={running} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_previous', 'Vorige')}</Button>}
                            {!result && (
                                <Button variant="contained" color={mode === 'install' ? 'warning' : 'primary'} disabled={running} onClick={run} sx={{ textTransform: 'none' }}>
                                    {mode === 'install' ? tr('cfg_setup_btn_install', 'Installeren') : tr('cfg_setup_btn_run', 'Uitvoeren')}
                                </Button>
                            )}
                            {result && result.ok && (
                                <Button variant="contained" sx={{ textTransform: 'none' }} onClick={() => {
                                    if (props.changeScreen) props.changeScreen('webshop-config');
                                    navigate('/' + props.lang + '/webshop-config');
                                }}>
                                    {tr('cfg_setup_btn_to_webshop', 'Naar webshop')}
                                </Button>
                            )}
                            {result && !result.ok && <Button onClick={() => setResult(null)} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_retry', 'Opnieuw')}</Button>}
                        </Stack>
                    </Box>
                )}
            </Box>
        </div>
    );
}
